/**
 * The certificate card on a test job (PT-4). It finds the job's certificate in
 * `GET /certificates` by job_id — the job read carries no certificate, and
 * there is no by-job certificate read — and opens the review on its own page
 * (/pressure-testing/certificates/:certificateId), whose back arrow returns
 * to the job.
 *
 * FOUR STATES, and the API's answer decides each:
 *  - the list did not load        → say so; never "no certificate"
 *  - the job has a certificate    → its status, number (or Draft), kind, flags
 *  - complete, no certificate     → "Generate certificate" (PT_DISPATCH) — a job
 *                                   completed before PT-4 has none, and drafts
 *                                   are not built for it automatically
 *  - not complete                 → it is drafted when the job is completed
 */
import React, { useState } from 'react';
import { FileText, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useDraftPtCertificate, usePtCertificates } from '../../hooks/usePressureTesting';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { ptCertKindSkin, ptCertStatusSkin } from '../../lib/pressure-testing';
import type { PtJobDetail } from '../../types/pressure-testing';
import { PT_SECONDARY, PtPanel, PtSkinBadge } from './PtBits';
import { ptWriteError } from './PtJobDialogs';
import { useOpenRecord } from '../downhole/recordPage';

export function PtJobCertificateCard({ job, canDispatch, timeZone }: { job: PtJobDetail; canDispatch: boolean; timeZone: string | null }) {
  const list = usePtCertificates();
  const draft = useDraftPtCertificate();
  const openRecord = useOpenRecord();
  const openCertificate = (id: string) => openRecord(`/pressure-testing/certificates/${id}`);
  const [error, setError] = useState<string | null>(null);
  const failure = queryLoadFailure(list);
  const cert = (list.data?.data ?? []).find((c) => c.job_id === job.id) ?? null;

  const generate = () => {
    setError(null);
    draft.mutate(
      { jobId: job.id },
      {
        onSuccess: (res) => {
          toast.success(`${job.job_number} certificate drafted`);
          if (res.data?.id) openCertificate(res.data.id);
        },
        onError: (err) => setError(ptWriteError(err, 'The certificate was not drafted.').message),
      },
    );
  };

  return (
    <PtPanel eyebrow="Pressure test certificate" title="Certificate" testId="pt-job-certificate">
      <div className="px-4 py-4">
        {list.isLoading ? (
          <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading the certificate" />
        ) : failure ? (
          <LoadFailureNotice error={queryFailureError(list)} kind={failure} subject="this job's certificate" />
        ) : cert ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-base font-extrabold text-white" data-testid="pt-job-cert-number">
                  {cert.certificate_number || 'Draft'}
                </span>
                <PtSkinBadge skin={ptCertStatusSkin(cert.status)} testId="pt-job-cert-status" value={cert.status} />
                <PtSkinBadge skin={ptCertKindSkin(cert.kind)} testId="pt-job-cert-kind" value={cert.kind} />
              </div>
              <p className="mt-1 text-sm text-fieldiq-text">
                {cert.status === 'issued'
                  ? `Issued ${formatInstant(cert.issued_at, timeZone) ?? '—'} by ${cert.issued_signatory_name || '—'}`
                  : cert.status === 'rejected'
                    ? `Rejected: ${cert.reject_reason || '—'}`
                    : `${typeof cert.flag_count === 'number' ? cert.flag_count : '—'} review flag${cert.flag_count === 1 ? '' : 's'} · awaiting issue`}
              </p>
            </div>
            <button type="button" className={PT_SECONDARY} onClick={() => openCertificate(cert.id)} data-testid="pt-job-cert-open">
              <FileText className="size-4" aria-hidden="true" />
              {cert.status === 'issued' ? 'View certificate' : 'Review certificate'}
            </button>
          </div>
        ) : job.status === 'complete' ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-fieldiq-text" data-testid="pt-job-cert-none">This job has no certificate yet.</p>
            {canDispatch && (
              <button
                type="button"
                className="fi-action fi-action--standard fi-action--primary"
                onClick={generate}
                disabled={draft.isPending}
                data-testid="pt-job-cert-generate"
              >
                {draft.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Plus className="size-4" aria-hidden="true" />}
                Generate certificate
              </button>
            )}
          </div>
        ) : (
          <p className="text-sm text-fieldiq-text" data-testid="pt-job-cert-pending">
            {job.status === 'cancelled' ? 'A cancelled job has no certificate.' : 'The certificate is drafted when this job is completed.'}
          </p>
        )}
        {error && (
          <p role="alert" className="mt-3 rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
            {error}
          </p>
        )}
      </div>
    </PtPanel>
  );
}
